import type { ApprovalDetail } from '@myrewards/contracts';
import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { actingForKeys } from '../../api/actingFor';
import { approvalKeys } from '../../api/ops';
import { isStale, problemWords } from '../labels';

/**
 * After a review edit, person move or decision: the saved detail with its new version replaces
 * the cached one, and the list, Home (Waiting on you and on others), the queue and what was done
 * for others follow.
 */
export async function refreshAfterApproval(
  queryClient: QueryClient,
  id: string,
  detail: ApprovalDetail,
): Promise<void> {
  queryClient.setQueryData(approvalKeys.one(id), detail);
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: approvalKeys.list }),
    queryClient.invalidateQueries({ queryKey: ['home'] }),
    queryClient.invalidateQueries({ queryKey: ['queue'] }),
    queryClient.invalidateQueries({ queryKey: actingForKeys.all }),
  ]);
}

/**
 * A review edit, person move or decision. The answer is the saved detail with the new version,
 * which replaces the cached one; the list, Home's Waiting on you and the queue follow. A stale
 * version (someone else acted first) reloads the detail before anything else is tried.
 */
export function useApprovalCommand<V>(
  id: string,
  run: (vars: V) => Promise<ApprovalDetail>,
  onDone?: (detail: ApprovalDetail, vars: V) => void | Promise<void>,
) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: run,
    onSuccess: async (detail, vars) => {
      setError(null);
      await refreshAfterApproval(queryClient, id, detail);
      await onDone?.(detail, vars);
    },
    onError: (e) => {
      setError(problemWords(e));
      if (isStale(e)) void queryClient.invalidateQueries({ queryKey: approvalKeys.one(id) });
    },
  });
  return {
    ...mutation,
    error,
    clearError: () => {
      setError(null);
    },
  };
}
